import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import DOMPurify from "dompurify";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import "../../../components/common/css/new.scss";
import "./addnew.scss";

// Defense-in-depth allowlist for the optional event URL. `type="url"` still
// accepts dangerous schemes (e.g. `javascript:`), so restrict the protocol to
// http/https via the URL() constructor — OWASP "Allow-list http and HTTPS URLs
// only". Returns "" for empty (valid, optional), the canonical href for a valid
// http(s) URL, or null for anything invalid. MUST be mirrored server-side and
// wherever the portal renders this value into an href.
function normalizeEventUrl(value) {
  const raw = (value || "").trim();
  if (!raw) return "";
  let parsed;
  try {
    parsed = new URL(raw);
  } catch (_) {
    return null; // unparseable or relative
  }
  return ["http:", "https:"].includes(parsed.protocol) ? parsed.href : null;
}

const NewsAddEdit = () => {
  const { newsId } = useParams();
  const isAdd = !newsId;

  const statusOptions = [
    { value: 0, label: "Draft" },
    { value: 1, label: "Published" },
  ];

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState([]);
  const [status, setStatus] = useState(statusOptions[0]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  // Optional Luma event URL. The portal renders this via a sandboxed
  // <LumaEmbed/> instead of accepting raw <script> in the description.
  const [eventUrl, setEventUrl] = useState("");
  // Optional date of the event itself, as YYYY-MM-DD for <input type="date">.
  // Drives the date shown on the public /news card and the list ordering.
  // Blank for ordinary articles, which fall back to their creation date.
  const [eventDate, setEventDate] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [existingBannerURL, setExistingBannerURL] = useState(null);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}admin/category-news`,
          {
            headers: { "x-access-token": token },
          }
        );
        const options = response.data.data.rows.map((d) => ({
          value: d.id,
          label: d.category,
        }));
        setCategories(options);
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Failed to load categories";
        Swal.fire({
          title: errorMessage,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };

    const fetchDataById = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API}admin/news/${newsId}`,
          {
            headers: { "x-access-token": token },
          }
        );
        const data = response.data.data;
        setTitle(data.title);
        setDescription(data.description);
        // Normalize on load so a legacy/non-conforming stored value (saved
        // before this allowlist existed) can't block saving the rest of the
        // form — a bad value is dropped to "" rather than weaponized.
        setEventUrl(normalizeEventUrl(data.event_url) || "");
        // The API returns an ISO timestamp; <input type="date"> wants the
        // bare calendar day. Slice the string rather than going through
        // Date(), which would shift the day for editors west of Greenwich.
        setEventDate(data.event_date ? String(data.event_date).slice(0, 10) : "");
        setStatus(statusOptions.find((opt) => opt.value === data.status));
        setCategoryId(data.category.map((cat) => cat.id));
        // FIX: Track existing banner URL separately, keep selectedFile null for edit mode
        setExistingBannerURL(data.bannerURL);
        setFilePreview(data.bannerURL);
        setSelectedFile(null);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        const errorMessage = error.response?.data?.message || error.message || "Failed to load news data";
        Swal.fire({
          title: errorMessage,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };

    fetchCategories();
    if (!isAdd) fetchDataById();
  }, [isAdd, newsId, token]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10485760) {
        // 10MB limit
        setErrors({ ...errors, image: "Image size should be less than 10MB" });
      } else {
        setErrors({ ...errors, image: "" });
        setSelectedFile(file);
        setFilePreview(URL.createObjectURL(file));
      }
    } else {
      setErrors({ ...errors, image: "" });
      setSelectedFile(null);
      setFilePreview(null);
    }
  };

  const handleDeleteFile = () => {
    Swal.fire({
      title: "Replace image?",
      text: "Please select a new image to replace the current one.",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Choose new image",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setSelectedFile(null);
        setFilePreview(null);
        setExistingBannerURL(null);
        setErrors({ ...errors, image: "Please select a new image" });
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "Title is required";
    if (!description.trim()) newErrors.description = "Description is required";
    if (!categoryId.length) newErrors.category = "Category is required";
    if (!status) newErrors.status = "Status is required";
    // FIX: Check for either new file OR existing banner URL
    const hasImage = selectedFile || existingBannerURL;
    if (!hasImage) {
      newErrors.image = "Image is required";
    } else if (selectedFile && selectedFile.size > 10485760) {
      newErrors.image = "Image size should be less than 10MB";
    }
    if (normalizeEventUrl(eventUrl) === null) {
      newErrors.event_url = "Enter a valid http(s) URL, e.g. https://luma.com/event/…";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", DOMPurify.sanitize(description));
    formData.append("category", JSON.stringify(categoryId));
    formData.append("bannerType", "image");
    formData.append("status", JSON.stringify(status.value));
    // Optional event URL — validated to http(s) above; API normalises "" to null.
    formData.append("event_url", normalizeEventUrl(eventUrl) || "");
    // Send midday UTC, not midnight. The column is a plain DATETIME, and
    // anchoring at 00:00 lets any timezone skew between this browser, the
    // API process and MySQL push the stored value onto the previous or next
    // calendar day. Midday leaves ~12h of slack in both directions.
    // Always appended: the API preserves the stored date only when the key
    // is absent, so sending "" is how the form clears it deliberately.
    formData.append("event_date", eventDate ? `${eventDate}T12:00:00.000Z` : "");
    // FIX: Only append if selectedFile is an actual File object (not a URL string)
    if (selectedFile && selectedFile instanceof File) {
      formData.append("newBanner", selectedFile);
    }

    const config = {
      method: isAdd ? "post" : "patch",
      url: `${process.env.REACT_APP_API}admin/news${isAdd ? "" : "/" + newsId}`,
      headers: {
        "x-access-token": token,
        // FIX: DO NOT set Content-Type manually - Axios auto-generates it with boundary for FormData
        // Setting it explicitly breaks Multer's file parsing (missing boundary parameter)
      },
      data: formData,
    };

    try {
      setLoading(true);
      const response = await axios(config);
      setLoading(false);
      Swal.fire({
        title: response.data.message,
        icon: "success",
        confirmButtonText: "OK",
      });
      navigate("/news");
    } catch (error) {
      setLoading(false);
      const errorMessage = error.response?.data?.message || error.message || "Failed to save news";
      Swal.fire({
        title: errorMessage,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <PageShell>
      <PageHead title={isAdd ? "Add news" : "Edit news"} />
      <div className="new">
      <div className="newContainer">
        <Loader isLoading={loading} />
        <div className="bottom">
          <div>
            <form onSubmit={handleSubmit} className="faq-form">
              <div className="custome-form-group">
                <label>Title*:</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setErrors({ ...errors, title: "" });
                  }}
                  className="custome-input"
                  style={{ width: "524px" }}
                />
                {errors.title && (
                  <div className="invalid-feedback">{errors.title}</div>
                )}
              </div>
              <div className="custome-form-group"></div>
              <div className="custome-form-group">
                <label>Category*:</label>
                <Select
                  isMulti
                  options={categories}
                  value={categories.filter((cat) =>
                    categoryId.includes(cat.value)
                  )}
                  onChange={(selected) => {
                    setCategoryId(
                      selected ? selected.map((sel) => sel.value) : []
                    );
                    setErrors({ ...errors, category: "" });
                  }}
                  className="sort-by dropdown country-drp"
                />
                {errors.category && (
                  <div className="invalid-feedback">{errors.category}</div>
                )}
              </div>
              <div className="custome-form-group"></div>
              <div className="custome-form-group">
                <label>Status*:</label>
                <Select
                  options={statusOptions}
                  value={status}
                  onChange={(selected) => {
                    setStatus(selected);
                    setErrors({ ...errors, status: "" });
                  }}
                  className="sort-by dropdown country-drp"
                />
                {errors.status && (
                  <div className="invalid-feedback">{errors.status}</div>
                )}
              </div>
              <div className="custome-form-group"></div>
              <div className="custome-form-group">
                <label>Banner Image*:</label>
                <input type="file" onChange={(e) => handleFileChange(e)} />
                {errors.image && (
                  <div className="invalid-feedback">{errors.image}</div>
                )}
              </div>
              {filePreview && (
                <div className="image-preview-container">
                  <div className="image-preview">
                    <img
                      src={filePreview}
                      alt="File Preview"
                      style={{ maxWidth: "200px" }}
                    />
                  </div>
                  <button
                    type="button"
                    className="delete-image"
                    onClick={handleDeleteFile}
                  >
                    Replace Image
                  </button>
                </div>
              )}
              <div className="custome-form-group" style={{ width: "95%" }}>
                <label>Description*:</label>
                <CKEditor
                  editor={ClassicEditor}
                  data={description}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setDescription(data);
                    setErrors({
                      ...errors,
                      description: data.trim() ? "" : "Description is required",
                    });
                  }}
                  config={{height:'300px'}}
                />
                {errors.description && (
                  <div className="invalid-feedback">{errors.description}</div>
                )}
              </div>
              <div className="custome-form-group" style={{ width: "95%" }}>
                <label>Event URL (optional):</label>
                <input
                  type="url"
                  className="form-control"
                  placeholder="https://luma.com/event/..."
                  value={eventUrl}
                  onChange={(e) => {
                    setEventUrl(e.target.value);
                    setErrors({ ...errors, event_url: "" });
                  }}
                />
                <small className="text-muted">
                  Paste a Luma event URL to surface a "Register for Event"
                  button on the public news page. Only http(s) links are
                  accepted; leave blank for a regular news article.{" "}
                  <strong>Do not</strong> paste embed scripts into the
                  description — they are stripped for security.
                </small>
                {errors.event_url && (
                  <div className="invalid-feedback">{errors.event_url}</div>
                )}
              </div>
              <div className="custome-form-group" style={{ width: "95%" }}>
                <label>Event date (optional):</label>
                <input
                  type="date"
                  className="form-control"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                />
                <small className="text-muted">
                  The date the event takes place. This is what the public
                  News page shows on the card, and what it sorts by, newest
                  event first. Leave blank for an ordinary article: it then
                  falls back to the date the article was created.
                </small>
              </div>
              <button type="submit" className="approveButton">
                {isAdd ? "Save" : "Update"}
              </button>
            </form>
          </div>
        </div>
      </div>
      </div>
    </PageShell>
  );
};

export default NewsAddEdit;
